import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Grid,
  makeStyles,
  FormControl,
  TextField,
  Button,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,  
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { NavLink as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { kmMessages } from "src/constant/Messages";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import { fillMissingTags } from "src/utils/utils";

const usernameMaxLength = 10;
const fnameMaxLength = 100;
const lnameMaxLength = 100;
const emailMaxLength = 100;
const addinfoMaxLength = 1000;

const formValidation = {
  required: kmMessages.error.form_validation.field_required,
  maxLength: `${kmMessages.error.form_validation.field_maxlengthnum}`,
  pattern: `${kmMessages.error.form_validation.field_wrongemail}`,
  validate: kmMessages.error.form_validation.field_required
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(2),
    },
  },
  formcontrol: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  paddingbottom: {
    paddingBottom: theme.spacing(1),
  },
  paddingtop: {
    paddingTop: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const Form = ({
  onSubmit,
  profiles,
  institutions,
  workingGroups,
  functions,
  user,
  editMode,
  areDictsLoaded,
  activeDate,
  setActiveDate,
  ...rest
}) => {
  const classes = useStyles();
  const [activeInf, setActiveInf] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [selectedWorkingGroups, setSelectedWorkingGroups] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [status, setStatus] = useState(null);

  const [representedSide, setRepresentedSide] = useState(null);

  const { handleSubmit, setValue, control, errors } = useForm();

  useEffect(() => {

    if (editMode) {
      if (user) {
        setValue("username", user.username);
        setValue("fname", user.firstName);
        setValue("lname", user.lastName);
        setValue("email", user.email);
        setValue("profiles", user.profiles);
        setSelectedProfiles(user.profiles);
        setValue("institution", user.institution);
        setSelectedInstitution(user.institution);
        if (user.institution && user.institution.sideRepresentedName){
          setRepresentedSide(user.institution.sideRepresentedName);
        }
        setValue("funct", user.function);
        setSelectedFunction(user.function);
        setValue("workinggroups", user.workingGroups);
        setSelectedWorkingGroups(user.workingGroups);
        setValue("addinfo", user.additionalInfo ? user.additionalInfo : "");
        let statusValue = user.active ? "active" : "locked";
        setValue("status", statusValue);
        setSelectedStatus(statusValue);
        setStatus(statusValue);
        setActiveInf(user.activeNoTimeLimit);
        setValue("activeinf", user.activeNoTimeLimit);
        if (!user.activeNoTimeLimit && user.activeUntil) {
          setActiveDate(moment(user.activeUntil));
        }
      }
    }
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, [user, areDictsLoaded]);

  const handleActiveInfChange = (event, parentProps) => {
    setActiveInf(event.target.checked);
    parentProps.onChange(event.target.checked);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleActiveUntilDateChange = (date) => {
    setActiveDate(date);
  };

  const handleInstitutionChange = (origChange, data) => {
    if (data && data.sideRepresentedName) {
      setRepresentedSide(data.sideRepresentedName);
    } else {
      setRepresentedSide(null);
    }
    origChange(data);
  };

  return (
    <Card className={clsx(classes.root)} {...rest}>
        <Box component="main">
          <Grid container className={classes.root}>
            <Grid item xs={12} elevation={2}>
              {areDictsLoaded ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <FormControl
                    fullWidth
                    className={classes.formcontrol}
                  >
                    <Controller
                      as={
                        <TextField
                          error={errors.username ? true : false}
                          id="username"
                          name="username"
                          label="Login"
                          placeholder="Login"
                          helperText={
                            errors.username &&
                            (
                              formValidation[errors.username.type] || ""
                            ).replaceAll("{maxlen}", usernameMaxLength)
                          }
                        />
                      }
                      name="username"
                      defaultValue=""
                      control={control}
                      rules={{ required: true, maxLength: usernameMaxLength }}
                    />
                  </FormControl>
                  <FormControl
                    fullWidth
                    className={classes.formcontrol}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} sm={6}>
                        <Controller
                          as={
                            <TextField
                              fullWidth
                              error={errors.fname ? true : false}
                              id="fname"
                              name="fname"
                              label="Imię"
                              placeholder="Imię"
                              helperText={
                                errors.fname &&
                                (
                                  formValidation[errors.fname.type] || ""
                                ).replaceAll("{maxlen}", fnameMaxLength)
                              }
                            />
                          }
                          name="fname"
                          defaultValue=""
                          control={control}
                          rules={{ required: true, maxLength: fnameMaxLength }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} sm={6}>
                        <Controller
                          as={
                            <TextField
                              fullWidth
                              error={errors.lname ? true : false}
                              id="lname"
                              name="lname"
                              label="Nazwisko"
                              placeholder="Nazwisko"
                              helperText={
                                errors.lname &&
                                (
                                  formValidation[errors.lname.type] || ""
                                ).replaceAll("{maxlen}", lnameMaxLength)
                              }
                            />
                          }
                          name="lname"
                          defaultValue=""
                          control={control}
                          rules={{ required: true, maxLength: lnameMaxLength }}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                  <FormControl
                    fullWidth
                    className={classes.formcontrol}
                  >
                    <Controller
                      as={
                        <TextField
                          error={errors.email ? true : false}
                          id="email"
                          name="email"
                          label="E-mail"
                          placeholder="E-mail"
                          helperText={
                            errors.email &&
                            (
                              formValidation[errors.email.type] || ""
                            ).replaceAll("{maxlen}", emailMaxLength)
                          }
                        />
                      }
                      name="email"
                      defaultValue=""
                      control={control}
                      rules={{
                        required: true,
                        maxLength: emailMaxLength,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      }}
                    />
                  </FormControl>
                  <FormControl fullWidth className={classes.formcontrol}>
                    {profiles && profiles.length > 0 ? (
                      <Controller
                        render={({ onChange, ...props }) => (
                          <Autocomplete
                            multiple
                            id="profiles-id"
                            options={profiles}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              value && option.id === value.id
                            }
                            value={props.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={errors.profiles ? true : false}
                                label="Profile"
                                placeholder="Profile"
                                helperText={
                                  errors.profiles &&
                                  (formValidation[errors.profiles.type] || "")
                                }
                              />
                            )}
                            onChange={(e, data) => onChange(data)}
                          />
                        )}
                        onChange={([, data]) => data}
                        name="profiles"
                        control={control}
                        rules={{ required: true, validate: value => value.length > 0 }}
                        defaultValue={selectedProfiles}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </FormControl>

                  <FormControl fullWidth className={classes.formcontrol}>
                    {institutions && institutions.length > 0 ? (
                      <Box>
                        <Controller
                          render={({ onChange, ...props }) => (
                            <Autocomplete
                              id="institution-id"
                              options={institutions}
                              getOptionLabel={(option) => option.name}
                              getOptionSelected={(option, value) =>
                                value && option.id === value.id
                              }
                              value={props.value}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={errors.institution ? true : false}
                                  label="Instytucja"
                                  placeholder="Instytucja"
                                  onChange={(e, data) => handleInstitutionChange(onChange,data)}
                                  helperText={
                                    errors.institution &&
                                    (formValidation[errors.institution.type] ||
                                      "")
                                  }
                                />
                              )}
                              onChange={(e, data) =>
                                handleInstitutionChange(onChange, data)
                              }
                            />
                          )}
                          onChange={([, data]) => data}
                          name="institution"
                          control={control}
                          rules={{ required: true }}
                          defaultValue={selectedInstitution}
                        />
                        {representedSide ? (
                          <FormHelperText id="institution-helper">
                            Reprezentowana strona: {representedSide}
                          </FormHelperText>
                        ) : (
                          <></>
                        )}
                      </Box>
                    ) : (
                      <Skeleton />
                    )}
                  </FormControl>
                  {/* <FormControl className={classes.formcontrol}>
                    <FormLabel component="label">
                      Reprezentowana strona:
                    </FormLabel>
                    <Typography>{representedSide}</Typography>
                  </FormControl> */}
                  <FormControl fullWidth className={classes.formcontrol}>
                    {functions && functions.length > 0 ? (
                      <Controller
                        render={({ onChange, ...props }) => (
                          <Autocomplete
                            id="funct-id"
                            options={functions}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              value && option.id === value.id
                            }
                            value={props.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={errors.funct ? true : false}
                                label="Funkcja"
                                placeholder="Funkcja"
                                onChange={(e, data) => onChange(data)}
                                helperText={
                                  errors.funct &&
                                  (formValidation[errors.funct.type] || "")
                                }
                              />
                            )}
                            onChange={(e, data) => onChange(data)}
                          />
                        )}
                        onChange={([, data]) => data}
                        name="funct"
                        control={control}
                        rules={{ required: true }}
                        defaultValue={selectedFunction}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </FormControl>
                  <FormControl fullWidth className={classes.formcontrol}>
                    {workingGroups ? (
                      <Controller
                        render={({ onChange, ...props }) => (
                          <Autocomplete
                            multiple
                            id="workinggroups-id"
                            options={workingGroups}
                            getOptionLabel={(option) => option.name}
                            getOptionSelected={(option, value) =>
                              value && option.id === value.id
                            }
                            value={props.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={errors.workinggroups ? true : false}
                                label="Grupy robocze"
                                placeholder="Grupy robocze"
                                helperText={
                                  errors.workinggroups &&
                                  (formValidation[errors.workinggroups.type] ||
                                    "")
                                }
                              />
                            )}
                            onChange={(e, data) => onChange(data)}
                          />
                        )}
                        onChange={([, data]) => data}
                        name="workinggroups"
                        control={control}
                        rules={{ required: true }}
                        defaultValue={selectedWorkingGroups}
                      />
                    ) : (
                      <Skeleton />
                    )}
                  </FormControl>
                  <FormControl
                    fullWidth
                    className={classes.formcontrol}
                  >
                    <Controller
                      as={
                        <TextField
                          error={errors.addinfo ? true : false}
                          id="addinfo"
                          name="addinfo"
                          label="Informacje dodatkowe"
                          placeholder="Informacje dodatkowe"
                          multiline
                          helperText={
                            errors.addinfo &&
                            (formValidation[errors.addinfo.type] || "")
                          }
                        />
                      }
                      name="addinfo"
                      defaultValue=""
                      control={control}
                      rules={{ maxLength: addinfoMaxLength }}
                    />
                  </FormControl>
                  <FormControl
                    component="fieldset"
                    className={classes.formcontrol}
                  >
                    <FormLabel component="legend">Status:</FormLabel>
                    <Controller
                      as={
                        <RadioGroup
                          row
                          aria-label="status"
                          name="status"
                        >
                          <FormControlLabel
                            value="active"
                            control={<Radio color="primary" onChange={handleStatusChange} />}
                            label="Aktywny"
                          />
                          <FormControlLabel
                            value="locked"
                            control={<Radio color="primary" onChange={handleStatusChange} />}
                            label="Zablokowany"
                          />
                        </RadioGroup>
                      }
                      name="status"
                      defaultValue={selectedStatus}
                      control={control}
                    />
                  </FormControl>
                  <FormControl
                    fullWidth
                    className={classes.formcontrol}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={12} sm={6}>
                        <DatePicker
                          disabled={activeInf || (status === "locked")}
                          id="acivedate"
                          autoOk
                          ampm={false}
                          fullWidth
                          label="Data aktywności konta"
                          clearable
                          value={activeDate}
                          placeholder="01/01/2025"
                          onChange={(date) => handleActiveUntilDateChange(date)}
                          format="DD/MM/yyyy"
                          animateYearScrolling
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Controller
                              name="activeinf"
                              disabled={status === "locked"}
                              control={control}
                              defaultValue={activeInf}
                              render={(props) => (
                                <Checkbox
                                  {...props}
                                  disabled={status === "locked"}
                                  id="activeinf"
                                  checked={activeInf}
                                  onChange={(e) =>
                                    handleActiveInfChange(e, props)
                                  }
                                  name="activeinf"
                                  color="primary"
                                />
                              )}
                            />
                          }
                          label="aktywność konta - czas nieokreślony"
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                  <Box className={classes.paddingtop}>
                    <Button
                      color="primary"
                      className={classes.button}
                      component={RouterLink}
                      to="/uzytkownicy"
                    >
                      Anuluj
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      className={classes.button}
                      endIcon={<SaveIcon />}
                      type="submit"
                    >
                      Zapisz
                    </Button>
                  </Box>
                </form>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        </Box>
    </Card>
  );
};

export default Form;
